import React, { useEffect, useMemo, useState } from "react"
import { useDispatch, useSelector } from "react-redux"
import { useNavigate } from "react-router"

import { UiCarousel } from "components/Carousel/Index"
import { SURVEY_PAGE } from "core/constants"
import { cdn } from "core/cdn"
import { useLanguage } from "locales"
import { DoctorType } from "types/entity"
import { ClientStore, ConfigurationStore } from "types/store"

import { CHECKUP_QUESTIONS } from "containers/Survey/checkup_models"
import { DERMATO_QUESTIONS } from "containers/Survey/dermato_models"
import { INITIAL_PREVENTION_QUESTIONS, PATH, QUESTIONS } from "containers/Survey/models"
import actions from "containers/Survey/services/actions"
import {
  QUESTION_PATIENT,
  QUESTION_RELATIVES,
} from "containers/Survey/services/constants"
import { Reply } from "containers/Survey/types"
import { request } from "lib/request"

const ChooseSpecialist: React.FC<{ startDiagnostic: (proceed: () => void) => void, loading: boolean }> = ({ startDiagnostic, loading }): JSX.Element => {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const { languages } = useLanguage()
  const [selected, setSelected] = useState<Array<DoctorType>>([])
  const [generalistEta, setGeneralistEta] = useState<string | undefined>("-");

  useEffect(() => {
    const fetchEtas = () => {
      request('/calls/eta', { method: 'GET' })
        .then((res: { data: { displayed: string } }) => {
          if (res?.data?.displayed) {
            setGeneralistEta((prev) =>
              res.data.displayed !== prev ? res.data.displayed : prev,
            )
          }
        })
        .catch((err) => console.error(err))
    }

    fetchEtas()
    const waitingTimeInterval = setInterval(fetchEtas, 60 * 1000)
    return () => clearInterval(waitingTimeInterval)
  }, [])

  const canAccessDermatology = useSelector(
    ({ configuration }: { configuration: ConfigurationStore }) =>
      !!configuration?.isAuthorizedForDermatologyBetaTest,
  )
  const canAccessBilanPrevention = useSelector(
    ({ configuration }: { configuration: ConfigurationStore }) =>
      !!configuration?.isAuthorizedForBilanPreventionBetaTest,
  )
  const customer = useSelector(
    ({ client }: { client: ClientStore }) => client.customer,
  )
  const relatives = useSelector(
    ({ client }: { client: ClientStore }) => client.relatives || [],
  )

  const pathways = useMemo(() => {
    const pathways: Reply[] = [
      {
        title: languages.generalistDoctor,
        subtitle: `${languages.estimatedWaitingTime}${generalistEta}`,
        value: DoctorType.GENERALIST,
        imgUrl: cdn("/images/tlc_terminee_illustration.svg"),
        ui: { type: "primary" },
      },
      {
        title: languages.checkupHealth,
        subtitle: languages.chooseSpecialistCheckupDelay,
        value: DoctorType.CHECKUP,
        imgUrl: cdn("/images/checkup/checkup.svg"),
      },
    ]

    if (canAccessBilanPrevention) {
      const bilanPreventionReply: Reply = {
        title: `${languages.prevention}`,
        value: DoctorType.PHARMACIST,
        imgUrl: cdn("/images/prevention/prevention.svg"),
      }
      pathways.push(bilanPreventionReply)
    }

    if (canAccessDermatology) {
      pathways.splice(1, 0, {
        title: languages.teleexpertise,
        subtitle: languages.dermatologyCheckupDelay,
        value: DoctorType.DERMATOLOGY,
        imgUrl: cdn("/images/dermatology/dermatologist.svg"),
        ui: { buttonStyle: { maxWidth: "unset" } },
      })
    }

    return pathways
  }, [canAccessDermatology, canAccessBilanPrevention, languages, generalistEta])

  const requiresDeviceCheck = (pathway: DoctorType) =>
    pathway === DoctorType.GENERALIST || pathway === DoctorType.DERMATOLOGY

  const onSelectPathway = (reply: Reply) => {
    const pathway = reply.value as DoctorType
    setSelected([pathway])

    const proceed = () => {
      dispatch(actions.reset())
      if (pathway === DoctorType.PHARMACIST) {
        dispatch(actions.replaceQuestions(INITIAL_PREVENTION_QUESTIONS))
      } else if (pathway === DoctorType.DERMATOLOGY) {
        dispatch(actions.replaceQuestions(DERMATO_QUESTIONS))
      } else if (pathway === DoctorType.CHECKUP) {
        dispatch(actions.replaceQuestions(CHECKUP_QUESTIONS))
      } else {
        dispatch(actions.replaceQuestions(QUESTIONS))
      }
      // Reinject customer and relatives in case of patwhay change
      if (customer) {
        dispatch({ type: QUESTION_PATIENT, payload: customer })
      }
      dispatch({ type: QUESTION_RELATIVES, payload: relatives })
      dispatch(actions.setStepReplies({ replies: [reply], id: PATH.doctor_type }))
      navigate(SURVEY_PAGE)
    }

    if (requiresDeviceCheck(pathway)) {
      startDiagnostic(proceed)
    } else {
      proceed()
    }
  }


  return (
    <UiCarousel
      items={pathways}
      selected={selected}
      onSelect={onSelectPathway}
      unSelect={onSelectPathway}
      ui={{ view: "radiogroup", itemByRow: 2, preventAddMore: true }}
    />
  )
}

export default ChooseSpecialist